import { computed, Injectable, inject } from "@angular/core";
import { db, JobIssue, JobSignature, Media } from "../../../core/database/db";
import { BqInspectionDataService } from "./bq-inspection-data.service";
import { JobIssueService } from "../../jobIssues/services/job-issue-service";
import { JobSignatureService } from "../../jobSignatures/services/job-signature-service";
import { ChecklistAreaGroup, ChecklistComponentEntry } from "../../../shared/services/checklist.service";
import { UNASSIGNED_AREA } from "../../areas/models/AreaDTO";
import { withAssignToMeOption } from "../../vendors/models/VendorDTO";

/** A punchlist item plus whatever's being added/changed on it, as emitted by NewIssueDrawerComponent. */
export interface SavedDiscussAndDemonstrateIssue {
  issue: JobIssue;
  addedImages: Media[];
  /** Selected trade partner id, or `SELF_ASSIGNED_VENDOR.id` when the CM chose "Assign to Me". */
  externalVendorId: number | undefined;
  newIssue: boolean;
}

/**
 * Data service for BQ1 ("Discuss") and BQ5 ("Demonstrate") - the tabbed punchlist + home-feature
 * checklist steps. Unlike BqInspectionSingleAreaDataService (BQ2/3/4's per-assertion pass/fail/NA
 * checklist), this step's punchlist issues are standalone JobIssues (not assertion-linked), and its
 * checklist tracks "component discussed with homeowner" completion instead.
 */
@Injectable()
export class BqInspectionDiscussAndDemonstrateDataService extends BqInspectionDataService {
  private readonly jobIssueServiceRef = inject(JobIssueService);
  private readonly jobSignatureService = inject(JobSignatureService);

  /** Non-deleted, not-yet-complete punchlist issues for this job. */
  readonly openIssues = computed<JobIssue[]>(() =>
    (this.jobIssues() ?? []).filter(issue => !issue.isComplete && !issue.deleted)
  );

  /** Area + component + assertion groups for the "reviewed with homeowner" checklist tab, built entirely
   * from already-loaded signals (no fresh IndexedDB reads). */
  readonly checklistGroups = computed<ChecklistAreaGroup[]>(() => {
    const areas = this.areas() ?? [];
    const components = this.components() ?? [];
    const assertions = this.assertions() ?? [];

    return areas
      .map(area => ({
        area,
        components: components
          .filter(c => c.areaId === area.id)
          .map(component => ({
            component,
            assertions: assertions.filter(a => a.componentId === component.id)
          }))
          .filter(entry => entry.assertions.length > 0)
          .sort((a, b) => a.component.name.localeCompare(b.component.name))
      }))
      .filter(group => group.components.length > 0);
  });

  /** True once every checklist item has been marked reviewed with the homeowner. */
  readonly checklistComplete = computed<boolean>(() =>
    this.checklistGroups().every(group => group.components.every(entry =>
      entry.assertions.length > 0 && entry.assertions.every(a => a.isComplete)
    ))
  );

  /** Persists a checklist item's toggled "reviewed with homeowner" state. */
  async updateChecklistComponentCompletion(entry: ChecklistComponentEntry, isComplete: boolean): Promise<void> {
    const updatedAt = new Date();
    const assertionIds = entry.assertions
      .map(a => a.id)
      .filter((id): id is number => id !== undefined);

    // Updated before the awaited DB write so checklistComplete() (and a same-tick "Final Review" tap)
    // sees this change immediately, instead of racing the IndexedDB write that backs it.
    this.assertions.update(assertions => (assertions ?? []).map(a =>
      (a.id !== undefined && assertionIds.includes(a.id) ? { ...a, isComplete, updatedAt } : a)
    ));

    await db.assertion.bulkUpdate(assertionIds.map(id => ({ key: id, changes: { isComplete, updatedAt } })));
  }

  /** Creates or updates a standalone punchlist issue (and its photos/assignee), as emitted by NewIssueDrawerComponent. */
  async saveIssue(saved: SavedDiscussAndDemonstrateIssue): Promise<JobIssue> {
    const selectedVendor = saved.externalVendorId
      ? withAssignToMeOption(this.vendors() ?? []).find(v => v.id === saved.externalVendorId) ?? null
      : null;

    if (saved.newIssue) {
      const savedIssue = await this.addIssue(saved.issue, saved.addedImages);
      await this.addOrReplaceVendorAssignmentForIssue(savedIssue, selectedVendor, null);
      return this.findJobIssue(savedIssue.identifier) ?? savedIssue;
    }

    const existingIssue = (this.jobIssues() ?? []).find(i => i.id === saved.issue.id);
    await this.updateIssue(saved.issue.id!, saved.issue.lotAreaIdentifier, saved.issue.text, saved.addedImages);

    if (!existingIssue) {
      return saved.issue;
    }

    await this.addOrReplaceVendorAssignmentForIssue(existingIssue, selectedVendor, this.getVendorAssignmentForIssue(existingIssue));
    return this.findJobIssue(existingIssue.identifier) ?? saved.issue;
  }

  private findJobIssue(identifier: string): JobIssue | undefined {
    return (this.jobIssues() ?? []).find(i => i.identifier === identifier);
  }

  private async addIssue(issue: JobIssue, media: Media[]): Promise<JobIssue> {
    const savedIssue = await this.jobIssueServiceRef.saveJobIssue(issue, media, null);

    this.jobIssues.update(issues => [...(issues ?? []), savedIssue]);
    this.media.update(mediaList => [...(mediaList ?? []), ...media.map(m => ({ ...m, parentIdentifier: savedIssue.identifier }))]);

    return savedIssue;
  }

  private async updateIssue(issueId: number, lotAreaIdentifier: string | undefined, text: string, media: Media[]): Promise<void> {
    const issueIdentifier = (this.jobIssues() ?? []).find(i => i.id === issueId)?.identifier;

    const mediaFileIdentifierToIdMap = await this.jobIssueServiceRef.updateJobIssue(issueId, { lotAreaIdentifier, text }, media);

    const updatedAt = new Date();
    this.jobIssues.update(issues => (issues ?? []).map(i =>
      (i.id === issueId ? { ...i, lotAreaIdentifier, text, updatedAt } : i)
    ));

    if (!issueIdentifier) {
      return;
    }

    const newMedia = media.filter(m => !m.id).map(m => ({
      ...m,
      id: mediaFileIdentifierToIdMap[m.fileIdentifier],
      parentIdentifier: issueIdentifier
    }));
    const deletedMediaIds = new Set(media.filter(m => m.deleted === 1 && m.id !== null && m.id !== undefined).map(m => m.id!));
    this.media.update(mediaList => [
      ...(mediaList ?? []).filter(m => !m.id || !deletedMediaIds.has(m.id)),
      ...newMedia
    ]);
  }

  /** Soft-deletes a punchlist issue (and its photos) - mirrors NewIssueDrawerComponent's existing delete flow. */
  async deleteIssue(issue: JobIssue): Promise<void> {
    await this.jobIssueServiceRef.deleteIssueWithRelatedRecords(issue);

    this.jobIssues.update(issues => (issues ?? []).filter(i => i.id !== issue.id));
    this.media.update(mediaList => (mediaList ?? []).filter(m => m.parentIdentifier !== issue.identifier));
    this.jobIssueVendors.update(vendors => (vendors ?? []).filter(v => v.issueIdentifier !== issue.identifier));
  }

  /** Soft-deletes a single photo attached to a punchlist issue. */
  async deleteIssueMedia(media: Media): Promise<void> {
    if (media.id === undefined) {
      return;
    }

    await db.softDeleteMedia([media.id]);
    this.media.update(mediaList => (mediaList ?? []).map(m => (m.id === media.id ? { ...m, deleted: 1 } : m)));
  }

  /** Saves the homeowner signature(s) captured on the final-review "confirm & sign" step. */
  async saveFinalReviewSignatures(signatures: JobSignature[]): Promise<void> {
    await this.jobSignatureService.bulkAddSignatures(signatures);
  }

  /** True for the "Unassigned Room" sentinel a room-select form control uses, or no selection at all. */
  isUnassignedArea(areaIdentifier: string | null | undefined): boolean {
    return areaIdentifier === UNASSIGNED_AREA.identifier || areaIdentifier === null || areaIdentifier === undefined;
  }

  /** Maps a stored (possibly unassigned) lot area identifier to the sentinel value a room-select form control expects. */
  toFormAreaIdentifier(areaIdentifier: string | null | undefined): string | null {
    if (this.isUnassignedArea(areaIdentifier)) {
      return UNASSIGNED_AREA.identifier;
    }

    return areaIdentifier ?? null;
  }

  /** Maps a room-select form control's value back to the stored (undefined-for-unassigned) lot area identifier. */
  toSavedAreaIdentifier(areaIdentifier: string | null | undefined): string | undefined {
    if (this.isUnassignedArea(areaIdentifier)) {
      return undefined;
    }

    return areaIdentifier ?? undefined;
  }
}
